import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { IHistoryEntry } from '@platform/core/types';
import { recordCode } from '../../form/header/recordCode';

/**
 * Підписи подій історії (прототип v4): «створив(-ла) запис» (англійською — «created this task»: `typeName` — однина типу чи вид запису з
 * малої, як recType прототипу), «змінив(-ла) «Статус»» (статус — як будь-яке поле), «прокоментував(-ла)», «пов’язано з 9» і «зв’язок з 9
 * вилучено» (номер іншого запису без префікса, як у стрічках Головної й аудиту). `other` — що підставити замість номера (рядок історії
 * робить з нього посилання).
 */
export function useHistoryText(typeName = '') {
  const { t } = useTranslation();
  const field = (e: IHistoryEntry) => e.fieldLabel ?? e.field ?? '';
  const lead = useCallback((e: IHistoryEntry, other?: string): string => {
    const ref = other ?? (e.otherDisplay ? recordCode(e.otherDisplay) : t('recordPage.history.deletedTarget'));
    switch (e.kind) {
      case 'created': return t('recordPage.history.created', { type: typeName });
      case 'changed':
        if (e.field === 'assignment' && e.to == null) return t('recordPage.history.assignmentRemoved');
        return e.to == null && e.from == null ? t('recordPage.history.updated', { field: field(e) }) : t('recordPage.history.changed', { field: field(e) });
      case 'status_changed': return t('recordPage.history.changed', { field: field(e) });
      case 'assigned': return e.field === 'assignment' ? t('recordPage.history.assignmentAdded') : t('recordPage.history.assigned');
      case 'comment': return t('recordPage.history.commented');
      case 'linked': return t('recordPage.history.linkedTo', { other: ref });
      case 'unlinked': return t('recordPage.history.unlinkedFrom', { other: ref });
      case 'file_added': return t('recordPage.history.fileAdded');
      case 'file_removed': return t('recordPage.history.fileRemoved');
      case 'deleted': return t('recordPage.history.deleted');
      case 'restored': return t('recordPage.history.restored');
      case 'acl_changed': return t('recordPage.history.acl');
      default: return t('recordPage.history.action');
    }
  }, [t, typeName]);
  const detail = useCallback((e: IHistoryEntry): string => {
    if (e.kind === 'comment') return e.to ?? '';
    if (e.kind === 'linked' || e.kind === 'unlinked') {
      const type = e.linkType ? t(`recordPage.linkTypes.${e.linkType}`, { defaultValue: e.linkType }) : '';
      return [type, [e.otherDisplay, e.otherTitle].filter(Boolean).join(' ') || t('recordPage.history.deletedTarget')].filter(Boolean).join(' · ');
    }
    return e.from != null || e.to != null ? `${field(e)}: ${e.from ?? '—'} → ${e.to ?? '—'}` : field(e);
  }, [t]);
  return { lead, detail };
}
